import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../components/Utils/Loader";
import PageContainer from "./PageContainer";
import "../assets/players.css";
import "../assets/transfer-portal.css";
import "../assets/players-ratings.css";
import FilterPop from "../components/FilterPop";
import PortalFilterPop from "../components/PortalFilterPop";
import { filterElements } from "../components/Utils/filterElements";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import UpdateIcon from "@mui/icons-material/Update";
import ClassIcon from "@mui/icons-material/Class";
import SortIcon from "@mui/icons-material/Sort";
import SchoolIcon from "@mui/icons-material/School";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { X_factor_getColor } from "../components/Utils/functions";

const Players = () => {
  const navigate = useNavigate();

  // Data/state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerList, setPlayerList] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleDateString()
  );

  // Filters and sorting
  const [selectedFilters, setSelectedFilters] = useState({
    region: [],
    state: [],
    position: [],
    class: [],
  });
  const [openFilter, setOpenFilter] = useState(null); // multi-select FilterPop
  const [openSort, setOpenSort] = useState(false); // single-select PortalFilterPop
  const [sortBy, setSortBy] = useState("xfactor"); // xfactor | name | lastname | ppg | rpg | apg | spg

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Fetch players (keep backend sort param behavior)
  useEffect(() => {
    setLoading(true);
    // Only pass supported backend sorts; stats sorts are handled client-side
    const backendSorts = new Set(["xfactor", "name", "lastname"]);
    const sortParam = backendSorts.has(sortBy) ? sortBy : "name";
    axios
      .get(`/api/players/?sortBy=${sortParam}`)
      .then((response) => {
        setPlayerList(response.data || []);
        setLastUpdated(new Date().toLocaleDateString());
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load players");
        toast.error("Failed to load players");
      })
      .finally(() => setLoading(false));
  }, [sortBy]);

  // Apply client-side filtering + search
  useEffect(() => {
    let list = [...playerList];

    // Search on player_name
    if (debouncedSearchTerm) {
      const q = debouncedSearchTerm.toLowerCase();
      list = list.filter((p) =>
        (p.player_name || "").toLowerCase().includes(q)
      );
    }

    // Filters
    const { state, region, position, class: gradClass } = selectedFilters;

    if (state.length > 0) {
      list = list.filter((p) => (p.state ? state.includes(p.state) : false));
    }
    if (region.length > 0) {
      list = list.filter((p) => (p.region ? region.includes(p.region) : false));
    }
    if (position.length > 0) {
      // HS positions may be full words; attempt basic includes match
      list = list.filter((p) =>
        p.position
          ? position.some((pos) =>
              p.position.toLowerCase().includes(pos.toLowerCase())
            )
          : false
      );
    }
    if (gradClass.length > 0) {
      list = list.filter((p) =>
        p.class ? gradClass.includes(p.class) : false
      );
    }

    // Local sort fallback (in case backend doesn't fully sort)
    list.sort((a, b) => {
      if (sortBy === "name") {
        return (a.player_name || "").localeCompare(b.player_name || "");
      }
      if (sortBy === "lastname") {
        const al = (a.player_name || "").split(" ").slice(-1)[0];
        const bl = (b.player_name || "").split(" ").slice(-1)[0];
        return al.localeCompare(bl);
      }
      if (
        sortBy === "ppg" ||
        sortBy === "rpg" ||
        sortBy === "apg" ||
        sortBy === "spg"
      ) {
        const aStats = getFakeStats(a);
        const bStats = getFakeStats(b);
        const key = sortBy;
        const av = typeof aStats[key] === "number" ? aStats[key] : 0;
        const bv = typeof bStats[key] === "number" ? bStats[key] : 0;
        return bv - av; // desc
      }
      // default xfactor desc
      const ax = parseFloat(a.xfactor) || 0;
      const bx = parseFloat(b.xfactor) || 0;
      return bx - ax;
    });

    setFilteredPlayers(list);
  }, [playerList, selectedFilters, debouncedSearchTerm, sortBy]);

  // Helpers
  const handlePlayerClick = (playerId) => {
    navigate(`/player/${playerId}`);
  };

  const handleFilterClick = (filterType) => {
    if (filterType === "sortOption") {
      setOpenSort(true);
    } else {
      setOpenFilter(filterType);
    }
  };

  const handleFilterClose = () => {
    setOpenFilter(null);
  };

  const handleFilterSave = (filterType, selectedValues) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: selectedValues,
    }));
  };

  const getFilterDisplayText = (filterType) => {
    const selected = selectedFilters[filterType];
    if (!selected || selected.length === 0) return "All";
    if (selected.length === filterElements[filterType].length) return "All";
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      setDebouncedSearchTerm(searchTerm);
    }
  };

  const sortOptions = useMemo(
    () => [
      { value: "xfactor", label: "X-Factor" },
      { value: "ppg", label: "Points (PPG)" },
      { value: "rpg", label: "Rebounds (RPG)" },
      { value: "apg", label: "Assists (APG)" },
      { value: "spg", label: "Steals (SPG)" },
      { value: "name", label: "First Name" },
      { value: "lastname", label: "Last Name" },
    ],
    []
  );

  const handleSortSave = (_key, value) => {
    setSortBy(value);
    setOpenSort(false);
  };

  // Fake stats generator (stable per player_id)
  const seeded = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i += 1)
      h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return () => {
      h ^= h << 13;
      h ^= h >>> 17;
      h ^= h << 5;
      return ((h >>> 0) % 1000) / 1000; // 0..1
    };
  };

  const getFakeStats = (player) => {
    const id =
      player.player_id || player.player_name || Math.random().toString();
    const rnd = seeded(String(id));
    const r1 = rnd();
    const r2 = rnd();
    const r3 = rnd();
    const r4 = rnd();
    const ppg = 6 + Math.round(r1 * 200) / 10; // 6.0 - 26.0
    const rpg = 1 + Math.round(r2 * 110) / 10; // 1.0 - 12.0
    const apg = Math.round(r3 * 80) / 10; // 0.0 - 8.0
    const spg = Math.round((r1 * 0.6 + r2 * 0.4) * 50) / 10; // 0.0 - 5.0
    const pct = 0.1 + r4 * 0.85; // 10% - 95%
    return { ppg, rpg, apg, spg, pct };
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="transfer-portal-loading">
          <Loader />
          <p>Loading player data...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="ncaa-ratings-container hs-players-scope">
        <div className="ncaa-ratings-header">
          <div className="ncaa-ratings-title">
            <h1>High School Ratings</h1>
          </div>

          <div className="ncaa-ratings-last-updated">
            <UpdateIcon />
            <span>Last Updated: {lastUpdated}</span>
          </div>

          <div className="ncaa-ratings-search">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
          </div>
        </div>

        <div className="ncaa-ratings-filters">
          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("state")}
          >
            <label>
              <FilterListIcon />
              <span>State</span>
            </label>
            <div className="filter-value">{getFilterDisplayText("state")}</div>
          </div>

          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("region")}
          >
            <label>
              <FilterListIcon />
              <span>Region</span>
            </label>
            <div className="filter-value">{getFilterDisplayText("region")}</div>
          </div>

          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("position")}
          >
            <label>
              <FilterListIcon />
              <span>Position</span>
            </label>
            <div className="filter-value">
              {getFilterDisplayText("position")}
            </div>
          </div>

          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("class")}
          >
            <label>
              <ClassIcon />
              <span>Class</span>
            </label>
            <div className="filter-value">{getFilterDisplayText("class")}</div>
          </div>

          <div
            className="ncaa-ratings-filter-group clickable-filter"
            onClick={() => handleFilterClick("sortOption")}
          >
            <label>
              <SortIcon />
              <span>Sort By</span>
            </label>
            <div className="filter-value">
              {(() => {
                const opt = sortOptions.find((o) => o.value === sortBy);
                return opt ? opt.label : "Sort";
              })()}
            </div>
          </div>
        </div>

        <FilterPop
          open={!!openFilter && openFilter !== "sortOption"}
          filterType={openFilter || "state"}
          onClose={handleFilterClose}
          onSave={handleFilterSave}
          selectedValues={openFilter ? selectedFilters[openFilter] : []}
        />

        <PortalFilterPop
          open={openSort}
          filterType="sortOption"
          onClose={() => setOpenSort(false)}
          onSave={(_type, value) => handleSortSave("sortOption", value)}
          selectedValue={sortBy}
          options={sortOptions}
          title="Sort By"
        />

        {error && (
          <div className="transfer-portal-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        <div className="transfer-portal-content">
          {filteredPlayers.length === 0 ? (
            <div className="no-results-box">
              <SearchIcon className="empty-icon" />
              <h3>Widen your criteria</h3>
              <p>We have no results for the filters selected below.</p>
              <div className="active-filters"></div>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDebouncedSearchTerm("");
                  setSelectedFilters({
                    region: [],
                    state: [],
                    position: [],
                    class: [],
                  });
                  setSortBy("xfactor");
                }}
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              <div className="transfer-portal-grid">
                {filteredPlayers.map((player, index) => {
                  const fake = getFakeStats(player);
                  return (
                    <div
                      key={player.player_id || index}
                      className="ncaa-ratings-card"
                      onClick={() => handlePlayerClick(player.player_id)}
                      style={{ "--animation-order": index % 50 }}
                    >
                      <div className="ncaa-ratings-card-header">
                        <div className="transfer-portal-player-info">
                          {player.image_url && (
                            <div
                              className="transfer-portal-player-image"
                              style={{ backgroundColor: "white" }}
                            >
                              <img
                                src={player.image_url}
                                alt={player.player_name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  const backupImages = [
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                                    "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                                  ];
                                  const randomBackup =
                                    backupImages[
                                      Math.floor(
                                        Math.random() * backupImages.length
                                      )
                                    ];
                                  e.target.src = randomBackup;
                                }}
                              />
                            </div>
                          )}
                          <div className="transfer-portal-player-name">
                            <h3>{player.player_name}</h3>
                            <div className="transfer-portal-player-details-row">
                              <span className="transfer-portal-position">
                                {player.position}
                              </span>
                              {player.class && (
                                <span className="transfer-portal-class">
                                  <ClassIcon fontSize="small" />
                                  {player.class}
                                </span>
                              )}
                            </div>
                            <div className="ncaa-ratings-rank-row">
                              <span className="ncaa-ratings-rank-label">
                                Rank:
                              </span>
                              <span className="ncaa-ratings-rank-value">-</span>
                            </div>
                          </div>
                        </div>
                        {(() => {
                          const rawX = player.xfactor;
                          const numericX =
                            typeof rawX === "number" ? rawX : parseFloat(rawX);
                          const hasValidX = Number.isFinite(numericX);
                          const roundedX = hasValidX
                            ? Math.round(numericX)
                            : null;
                          const displayX = hasValidX ? roundedX : "-";
                          const computedColor = hasValidX
                            ? X_factor_getColor(roundedX)
                            : undefined;
                          const colorX =
                            computedColor === "#000"
                              ? undefined
                              : computedColor;
                          return (
                            <div className="transfer-portal-xfactor">
                              <span>X-Factor</span>
                              <div
                                className="transfer-portal-xfactor-value"
                                style={{ color: colorX }}
                              >
                                {displayX}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      <div className="transfer-portal-card-body">
                        <div className="ncaa-ratings-school-info">
                          <div className="ncaa-ratings-school">
                            <SchoolIcon />
                            <span>{player.state || "-"}</span>
                          </div>
                          <div className="ncaa-ratings-conference">
                            <span className="ncaa-ratings-conference-label">
                              Region:
                            </span>
                            <span className="ncaa-ratings-conference-value">
                              {player.region || "-"}
                            </span>
                          </div>
                        </div>

                        <div className="ncaa-ratings-stats-row">
                          <div className="transfer-portal-stat-item">
                            <span className="transfer-portal-stat-value">
                              {fake.ppg.toFixed(1)}
                            </span>
                            <span className="transfer-portal-stat-label">
                              PPG
                            </span>
                          </div>
                          <div className="transfer-portal-stat-item">
                            <span className="transfer-portal-stat-value">
                              {fake.rpg.toFixed(1)}
                            </span>
                            <span className="transfer-portal-stat-label">
                              RPG
                            </span>
                          </div>
                          <div className="transfer-portal-stat-item">
                            <span className="transfer-portal-stat-value">
                              {fake.apg.toFixed(1)}
                            </span>
                            <span className="transfer-portal-stat-label">
                              APG
                            </span>
                          </div>
                          <div className="transfer-portal-stat-item">
                            <span className="transfer-portal-stat-value">
                              {Math.round(fake.pct * 100)}%
                            </span>
                            <span className="transfer-portal-stat-label">
                              Percentile
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="transfer-portal-card-footer ncaa-ratings-card-footer">
                        <button
                          className="ncaa-ratings-view-details"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayerClick(player.player_id);
                          }}
                          style={{ animation: "none" }}
                        >
                          <AssessmentIcon fontSize="small" />
                          <span>Advanced Metrics</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default Players;
